'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { PerformanceLabSection, PerfLabDiscount, PerfLabOffer } from './types';
import { planSections } from './plans';
import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  useIsIndiaRegion,
  usePerformanceLabAffiliateDiscount,
} from '../../hooks';
import { buildPerformanceLabCartLineItem } from './performanceLabCartUtils';
import { useTripleWhaleProductDetails } from 'hooks/analytics/useTripleWhaleProductView';

interface PerformanceLabsIndiaContextState {
  sections: PerformanceLabSection[];
  isLoading: boolean;
  error: string | null;
  /**
   * Pass the plan's ACTIVE offer (not just its code) so checkout can apply
   * exactly what the card displayed — see the precedence note on the
   * implementation.
   */
  addToCart: (
    shopifyVariantId: string,
    activeOffer?: PerfLabOffer | null,
  ) => Promise<void>;
  cart: Record<string, any> | null;
  updateCartQuantity: (
    clid: string,
    currentQuantity: number,
    attributes?: Array<Record<string, string>>,
  ) => Promise<void>;
  deleteFromCart: (clid: string) => Promise<void>;
  cartLoading: boolean;
  /**
   * Performance Labs affiliate discounts can be scoped to specific products, so
   * both of these take the plan's Shopify variant id. Omitting it answers for a
   * catalogue-wide discount only.
   */
  getPlanDiscount: (shopifyVariantId?: string) => PerfLabDiscount;
  getDiscountedPrice: (price: number, shopifyVariantId?: string) => number;
  isCartEnabled: boolean;
}

const PerformanceLabsIndiaContext = createContext<
  PerformanceLabsIndiaContextState | undefined
>(undefined);

interface PerformanceLabsIndiaProviderProps {
  children: ReactNode;
}

export const PerformanceLabsIndiaProvider: React.FC<
  PerformanceLabsIndiaProviderProps
> = ({ children }) => {
  const firstPlan = planSections[0]?.plans[0];
  useTripleWhaleProductDetails({
    name: 'Ultrahuman Performance Lab',
    variant: firstPlan?.shopifyVariantId,
    price: firstPlan?.price,
  });
  const cart = useCartStore(
    useShallow((s) => ({
      addToCartSilently: s.addToCartSilently,
      cart: s.cart,
      updateCartQuantity: s.updateCartQuantity,
      deleteFromCart: s.deleteFromCart,
      cartLoading: s.cartLoading,
    })),
  );
  // Cart/checkout is only wired to the India (INR) store. Outside India the
  // provider can still be mounted (e.g. the IV Lounge page renders for all
  // regions) but cards fall back to the "Notify Me" flow instead of add-to-cart.
  const isCartEnabled = useIsIndiaRegion();

  // Affiliate discount — resolved per plan, since a Performance Labs code can
  // be scoped to a subset of the catalogue.
  const { getPlanDiscount, getDiscountedPrice } =
    usePerformanceLabAffiliateDiscount();

  const addToCart = useCallback(
    async (shopifyVariantId: string, activeOffer?: PerfLabOffer | null) => {
      // Precedence must mirror what the card DISPLAYS, or we charge a price we
      // never showed. The card shows the offer price whenever an offer is
      // active (`hasDiscount = !activeOffer && ...`), so when one is active the
      // affiliate code must not override it — an affiliate's 10% attached over
      // a displayed 29%-off flat offer charges MORE than the price on screen.
      // Some flat offers carry no code at all (the Shopify price already
      // reflects them), in which case the correct payload is no code.
      const discountCode = activeOffer
        ? activeOffer.discountCode || undefined
        : getPlanDiscount(shopifyVariantId).discountCode || undefined;

      await cart.addToCartSilently([
        buildPerformanceLabCartLineItem(shopifyVariantId, discountCode),
      ]);
    },
    [cart, getPlanDiscount],
  );

  const contextValue: PerformanceLabsIndiaContextState = useMemo(
    () => ({
      sections: planSections,
      isLoading: false,
      error: null,
      addToCart,
      cart: cart.cart,
      updateCartQuantity: cart.updateCartQuantity,
      deleteFromCart: cart.deleteFromCart,
      cartLoading: cart.cartLoading,
      getPlanDiscount,
      getDiscountedPrice,
      isCartEnabled,
    }),
    [
      addToCart,
      cart.cart,
      cart.updateCartQuantity,
      cart.deleteFromCart,
      cart.cartLoading,
      getPlanDiscount,
      getDiscountedPrice,
      isCartEnabled,
    ],
  );

  return (
    <PerformanceLabsIndiaContext.Provider value={contextValue}>
      {children}
    </PerformanceLabsIndiaContext.Provider>
  );
};

export const usePerformanceLabsIndia = (): PerformanceLabsIndiaContextState => {
  const context = useContext(PerformanceLabsIndiaContext);
  if (context === undefined) {
    throw new Error(
      'usePerformanceLabsIndia must be used within a PerformanceLabsIndiaProvider',
    );
  }
  return context;
};
